import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, X, Building2, Pencil, Users, LogIn } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import JurisdictionLodgeUsersModal from "./JurisdictionLodgeUsersModal";

const emptyForm = {
  name: "",
  lodge_number: "",
  address: "",
  founded_date: "",
};

/**
 * Panel embedded in the Users page. Lets a tenant admin manage the lodges
 * created under their obedience jurisdiction. The obedience abbreviation is
 * pulled from the parent tenant and shown as read-only context.
 */
export default function JurisdictionLodgesPanel() {
  const { t, i18n } = useTranslation();
  const locale =t("jurisdictionLodgesPanel.en_gb");
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { enterChildLodge } = useAuth();

  const [obedienceInfo, setObedienceInfo] = useState({ obedience: null, obedience_abbreviation: null });
  const [lodges, setLodges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [usersFor, setUsersFor] = useState(null);

  const load = async () => {
    try {
      const [me, list] = await Promise.all([
        api.get("/jurisdiction-lodges/me"),
        api.get("/jurisdiction-lodges"),
      ]);
      setObedienceInfo(me.data || {});
      setLodges(list.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({
      name: l.name || "",
      lodge_number: l.lodge_number || "",
      address: l.address || "",
      founded_date: l.founded_date || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/jurisdiction-lodges/${editing.id}`, form);
        toast.success(t("jurisdiction.updatedSuccess"));
      } else {
        await api.post("/jurisdiction-lodges", form);
        toast.success(t("jurisdiction.createdSuccess"));
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l) => {
    const ok = await confirm({
      title: t("common.delete"),
      message: t("jurisdiction.deleteConfirm", { name: l.name }),
    });
    if (!ok) return;
    try {
      await api.delete(`/jurisdiction-lodges/${l.id}`);
      toast.success(t("jurisdiction.deletedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const abbrev = obedienceInfo?.obedience_abbreviation || obedienceInfo?.obedience || "—";

  return (
    <div className="mt-12" data-testid="jurisdiction-lodges-panel">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="page-title" style={{ fontSize: "1.5rem" }}>
            {t("jurisdiction.title")}
          </h2>
          <p className="page-subtitle">
            {t("jurisdiction.subtitle")}
            <span style={{ marginLeft: 8, color: "#9a7530", fontWeight: 600, letterSpacing: "0.08em" }}>
              · {abbrev}
            </span>
          </p>
        </div>
        <button
          className="btn-primary-gold flex items-center gap-2"
          onClick={openCreate}
          data-testid="add-jurisdiction-lodge-btn"
        >
          <Plus size={14} /> {t("jurisdiction.new")}
        </button>
      </div>

      <div className="gold-divider my-6" />

      <div className="overflow-x-auto border border-[var(--cream-200)]">
        <table className="lodge-table" data-testid="jurisdiction-lodges-table">
          <thead>
            <tr>
              <th>{t("jurisdiction.name")}</th>
              <th>{t("jurisdiction.number")}</th>
              <th>{t("jurisdiction.address")}</th>
              <th>{t("jurisdiction.founded")}</th>
              <th>{t("superadmin.obedience")}</th>
              <th className="text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {lodges.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", fontStyle: "italic", color: "#858481", padding: "2rem" }}>
                  {t("jurisdiction.empty")}
                </td>
              </tr>
            ) : (
              lodges.map((l) => (
                <tr key={l.id} data-testid={`jurisdiction-row-${l.id}`}>
                  <td>
                    <button
                      className="flex items-center gap-2"
                      onClick={() => window.location.assign(`/jurisdiction-lodges/${l.id}`)}
                      data-testid={`open-jurisdiction-${l.id}`}
                      style={{
                        background: "transparent",
                        border: 0,
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "Cinzel, serif",
                        color: "#0d1b2a",
                        fontSize: "0.95rem",
                      }}
                      title={t("jurisdiction.openOverview", "Vezi detalii")}
                    >
                      <Building2 size={16} color="#c9a84c" />
                      <span style={{ textDecoration: "underline", textDecorationColor: "#c9a84c", textUnderlineOffset: 3 }}>
                        {l.name}
                      </span>
                    </button>
                  </td>
                  <td>{l.lodge_number || "—"}</td>
                  <td>{l.address || "—"}</td>
                  <td>{l.founded_date ? new Date(l.founded_date).toLocaleDateString(locale) : "—"}</td>
                  <td>
                    <span className="badge-grade">{l.obedience_abbreviation || "—"}</span>
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        className="btn-primary-gold flex items-center gap-1"
                        onClick={() => {
                          enterChildLodge({ id: l.id, name: l.name });
                          navigate("/");
                        }}
                        data-testid={`enter-jurisdiction-${l.id}`}
                        title={t("jurisdiction.enterLodge", "Intră în lojă")}
                      >
                        <LogIn size={12} /> {t("jurisdiction.enterShort", "INTRĂ")}
                      </button>
                      <button
                        className="btn-outline-gold flex items-center gap-1"
                        onClick={() => setUsersFor(l)}
                        data-testid={`users-jurisdiction-${l.id}`}
                        title={t("jurisdiction.users.title")}
                      >
                        <Users size={12} /> {t("jurisdiction.users.short", "Utilizatori")}
                      </button>
                      <button
                        className="btn-outline-gold"
                        onClick={() => openEdit(l)}
                        data-testid={`edit-jurisdiction-${l.id}`}
                        title={t("common.edit")}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        className="btn-danger"
                        onClick={() => remove(l)}
                        data-testid={`delete-jurisdiction-${l.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            data-testid="jurisdiction-form"
            style={{ maxWidth: 520 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {editing ? t("jurisdiction.editTitle") : t("jurisdiction.new")}
              </h2>
              <button type="button" onClick={() => setShowForm(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label-lodge">{t("jurisdiction.name")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="jurisdiction-input-name"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-lodge">{t("jurisdiction.number")}</label>
                  <input
                    className="input-lodge"
                    value={form.lodge_number}
                    onChange={(e) => setForm({ ...form, lodge_number: e.target.value })}
                    placeholder="42"
                    data-testid="jurisdiction-input-number"
                  />
                </div>
                <div>
                  <label className="label-lodge">{t("jurisdiction.founded")}</label>
                  <input
                    type="date"
                    className="input-lodge"
                    value={form.founded_date}
                    onChange={(e) => setForm({ ...form, founded_date: e.target.value })}
                    data-testid="jurisdiction-input-founded"
                  />
                </div>
              </div>
              <div>
                <label className="label-lodge">{t("jurisdiction.address")}</label>
                <input
                  className="input-lodge"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="jurisdiction-input-address"
                />
              </div>
              <div>
                <label className="label-lodge">{t("superadmin.obedience")}</label>
                <input
                  className="input-lodge"
                  value={abbrev}
                  readOnly
                  disabled
                  style={{ background: "#f6efde", color: "#9a7530", fontWeight: 600, letterSpacing: "0.08em" }}
                  data-testid="jurisdiction-obedience-readonly"
                />
                <p className="kv-label" style={{ fontSize: "0.62rem", marginTop: "0.3rem" }}>
                  {t("jurisdiction.obedienceHint")}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="btn-primary-gold"
                disabled={saving}
                data-testid="jurisdiction-save-btn"
              >
                {saving ? "..." : editing ? t("common.save") : t("common.create")}
              </button>
            </div>
          </form>
        </div>
      )}
      {usersFor && (
        <JurisdictionLodgeUsersModal lodge={usersFor} onClose={() => setUsersFor(null)} />
      )}
    </div>
  );
}
